import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Container, Grid, Hidden } from '@material-ui/core';
import { Nav } from 'containers/common/sections/layout';
import { RetailStepperPagination } from 'components/retail/atoms';
import SelectedPlanCard from 'components/common/structures/SelectedPlanCard';
import retailStores from 'stores/retail';
import { NextRouter, useRouter } from 'next/router';
import CircularProgress from '@material-ui/core/CircularProgress';
import RetailTheme from 'containers/retail/layouts/RetailTheme';
import {
  RETAIL_RESIDENTIAL_CUSTOMER_INFORMATION_ROUTE,
  RETAIL_RESIDENTIAL_FLOW_CONFIRMATION_ROUTE,
  RETAIL_RESIDENTIAL_UTILITY_ACCOUNT_INFORMATION_ROUTE,
} from 'config/retail/residential/routes';
import {
  RETAIL_COMMERCIAL_BILLING_ADDRESS_ROUTE,
  RETAIL_COMMERCIAL_CUSTOMER_INFORMATION_ROUTE,
  RETAIL_COMMERCIAL_FLOW_CONFIRMATION_ROUTE,
  RETAIL_COMMERCIAL_UTILITY_ACCOUNT_INFORMATION_ROUTE,
} from 'config/retail/commercial/routes';
import MobilePlanDetailsDialog from 'containers/retail/sections/energy-rates/MobilePlanDetailsDialog';
import { CustomerType } from 'services/orders/types';
import RetailAppStepper from 'components/retail/atoms/RetailStepper/RetailAppStepper';
import RetailBreadcrumbs from 'components/retail/structures/RetailBreadcrumbs';
import useI18n from "evcharging/hooks/useI18n";

interface RetailLayoutProps {
  activeStep: number;
  onCompleteStep: () => void;
  children: ReactNode;
  isResidential?: boolean;
  isLoading?: boolean;
}

const residentialRoutes = [
  '',
  RETAIL_RESIDENTIAL_CUSTOMER_INFORMATION_ROUTE,
  RETAIL_RESIDENTIAL_UTILITY_ACCOUNT_INFORMATION_ROUTE,
  RETAIL_RESIDENTIAL_FLOW_CONFIRMATION_ROUTE,
];

const commercialRoutes = [
  '',
  RETAIL_COMMERCIAL_CUSTOMER_INFORMATION_ROUTE,
  RETAIL_COMMERCIAL_UTILITY_ACCOUNT_INFORMATION_ROUTE,
  RETAIL_COMMERCIAL_BILLING_ADDRESS_ROUTE,
  RETAIL_COMMERCIAL_FLOW_CONFIRMATION_ROUTE,
];

const RetailLayout: FC<RetailLayoutProps> = observer((props) => {
  const { onCompleteStep, children, activeStep, isResidential = false, isLoading = false } = props;
  const routes = isResidential ? residentialRoutes : commercialRoutes;
  const { commercialOrderStore, residentialOrderStore } = retailStores;
  const activeStore = useMemo(
    () => (isResidential ? residentialOrderStore : commercialOrderStore),
    [isResidential, residentialOrderStore, commercialOrderStore]
  );
  const [loading, setLoading] = useState<boolean>(true);
  const { push }: NextRouter = useRouter();
  const isFinalStep = activeStep + 1 === (isResidential ? 4 : 5);

  useEffect(() => {
    if (activeStore.isValidSession(activeStep)) {
      setLoading(false);
    } else {
      setLoading(false)
      push('/').then();
    }
  }, [activeStore]);

  const onBack = () => {
    return push(activeStore.getBackUrl());
  };

  const onChangePlan = () => {
    return push(activeStore.getBackUrl());
  };

  const onPreviousStep = () => {
    return push(routes[activeStep - 1]);
  };

  const [openMobileSelectedPlan, setOpenMobileSelectedPlan] = useState<boolean>(false);
  const onToggleMobileSelectedPlan = () => setOpenMobileSelectedPlan(!openMobileSelectedPlan);
  const onViewPlan = () => onToggleMobileSelectedPlan();

  const { t } = useI18n();

  if (loading) {
    return <CircularProgress />;
  }

  const rest =
    activeStep > 1
      ? {
          previousText: isFinalStep ? t('retail.edit-info') : t('retail.prev-step'),
          withBackArrow: true,
          previous: true,
          previousStep: onPreviousStep,
        }
      : {};


  return (
    <RetailTheme>
      <MobilePlanDetailsDialog
        open={openMobileSelectedPlan}
        plan={activeStore.getSelectedPlan()}
        onClose={onToggleMobileSelectedPlan}
        onComplete={onChangePlan}
        title={t('retail.selected-plan')}
        buttonText={t('retail.change-plan')}
        buttonVariant="outlined"
      />
      <Nav />

      <Hidden mdUp>
        <Container>
          <RetailBreadcrumbs
            customerType={isResidential ? CustomerType.RESIDENTIAL : CustomerType.COMMERCIAL}
          />
          <RetailAppStepper
            activeStep={activeStep}
            customerType={isResidential ? CustomerType.RESIDENTIAL : CustomerType.COMMERCIAL}
          />
        </Container>
      </Hidden>
      <Hidden smDown>
        <Box mb={18} />
        <Container>
          <RetailBreadcrumbs
            customerType={isResidential ? CustomerType.RESIDENTIAL : CustomerType.COMMERCIAL}
          />
          <RetailAppStepper
            activeStep={activeStep}
            customerType={isResidential ? CustomerType.RESIDENTIAL : CustomerType.COMMERCIAL}
          />
        </Container>
        <Box mb={5} />
      </Hidden>
      <Container>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4} lg={3}>
            <SelectedPlanCard
              selectedPlan={activeStore.getSelectedPlan()}
              onChangePlan={onChangePlan}
              onViewPlan={onViewPlan}
            />
          </Grid>
          <Grid item xs={12} md={8} lg={9}>
            {children}
            <RetailStepperPagination
              currentlyStepText={t('retail.step-of', { current:activeStep + 1, total: isResidential ? 4 : 5})}
              completeText={isFinalStep ? t('retail.place-order') : t('action.continue')}
              completeStep={onCompleteStep}
              finallyIcon={isFinalStep}
              isLoading={isLoading}
              disabledComplete={isLoading}
              {...rest}
            />
          </Grid>
        </Grid>
      </Container>
    </RetailTheme>
  );
});

export default RetailLayout;
